import React, { useState } from 'react';
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { Layers, Users, ChartColumn, Monitor } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../i18n/I18nContext';
import { postLoginPath } from '../../config/rolePermissions';
import AuthLayout from './AuthLayout';
import AuthField from './AuthField';
import AuthAlert from './AuthAlert';
import AuthSubmit from './AuthSubmit';
import useSignedInTarget from './useSignedInTarget';
import { authErrorMessage, canAutoFocus, focusField } from './authHelpers';

function Login() {
  const { t } = useI18n();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({}); // { identifier, password }: a field left empty
  const [error, setError] = useState(''); // the sentence for a refused sign-in
  const [submitting, setSubmitting] = useState(false);
  const [shake, setShake] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // The page that sent the user here (MentorProtectedRoute), with its filters.
  const fromLocation = location.state?.from;
  const returnTo = fromLocation?.pathname
    ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}`
    : null;
  // Already signed in (a bookmarked /login, the Back button): no form, straight on.
  const signedInTarget = useSignedInTarget({ returnTo, busy: submitting });

  if (signedInTarget) return <Navigate to={signedInTarget} replace />;

  const fail = (message) => {
    setError(message);
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShake(false);
    // Our own check, so each message is in the page language and sits under its field
    // (a browser bubble is neither).
    const missing = {};
    if (!identifier.trim()) missing.identifier = t('auth.login.errorIdentifier');
    if (!password) missing.password = t('auth.login.errorPassword');
    setFieldErrors(missing);
    if (missing.identifier || missing.password) {
      focusField(missing.identifier ? 'identifier' : 'password');
      return;
    }
    setSubmitting(true);
    try {
      const res = await login(identifier.trim(), password);
      navigate(
        postLoginPath({ returnTo, accountRole: res?.user?.role, staffRole: res?.staffRole }),
        { replace: true },
      );
    } catch (err) {
      fail(authErrorMessage(err, {
        badCredentials: t('auth.login.errorInvalidCredentials'),
        fallback: t('auth.login.errorDefault'),
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    { icon: <Layers size={18} />, title: t('auth.login.feature1Title'), desc: t('auth.login.feature1Desc') },
    { icon: <Users size={18} />, title: t('auth.login.feature2Title'), desc: t('auth.login.feature2Desc') },
    { icon: <ChartColumn size={18} />, title: t('auth.login.feature3Title'), desc: t('auth.login.feature3Desc') },
    { icon: <Monitor size={18} />, title: t('auth.login.feature4Title'), desc: t('auth.login.feature4Desc') },
  ];

  return (
    <AuthLayout
      title={t('auth.login.title')}
      tagline={t('auth.login.tagline1')}
      taglineEm={t('auth.login.taglineEm')}
      desc={t('auth.login.desc')}
      features={features}
      shake={shake}
      below={[
        <>{t('auth.login.noAccount')} <Link to="/register">{t('auth.login.requestAccess')}</Link></>,
        <>{t('auth.login.teacherPrompt')} <Link to="/teacher-login">{t('auth.login.teacherLink')}</Link></>,
      ]}
    >
      <div className="auth-form-header">
        <h1>{t('auth.login.title')}</h1>
        <p>{t('auth.login.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} noValidate aria-busy={submitting}>
        <AuthField
          id="identifier"
          label={t('auth.login.identifierLabel')}
          value={identifier}
          onChange={(e) => {
            setIdentifier(e.target.value);
            setError('');
            setFieldErrors((current) => ({ ...current, identifier: undefined }));
          }}
          placeholder={t('auth.login.identifierPlaceholder')}
          error={fieldErrors.identifier}
          invalid={!!error}
          describedBy="login-error"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus={canAutoFocus()}
          readOnly={submitting}
        />

        <AuthField
          id="password"
          type="password"
          label={t('auth.login.passwordLabel')}
          aside={<Link to="/forgot-password" className="auth-forgot-link">{t('auth.login.forgotPassword')}</Link>}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setError('');
            setFieldErrors((current) => ({ ...current, password: undefined }));
          }}
          placeholder={t('auth.login.passwordPlaceholder')}
          error={fieldErrors.password}
          invalid={!!error}
          describedBy="login-error"
          autoComplete="current-password"
          readOnly={submitting}
        />

        <AuthAlert id="login-error">{error}</AuthAlert>

        <AuthSubmit busy={submitting} label={t('auth.login.signIn')} busyLabel={t('auth.login.signingIn')} />
      </form>
    </AuthLayout>
  );
}

export default Login;
